import { useSelector } from 'react-redux';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    BarGradient,
    Box,
    DispersedSourceSolidIcon,
    ExpandMoreIcon,
    Typography,
    useTheme,
    Link,
    Grid,
    Button,
    Alert,
    Tab,
    TabPanel,
    MarkdownFormatter
} from '@methanesat/ui-components';

import { DataRowsModalBody } from '../../../EmissionsPageMap/LayerDataDrawer/Layout';
import { useGetDispersedSourceAccordionProps } from './utils';
import { DispersedSourceAccordionProps, Platforms, AreaEmissionsProducts } from '../../../../types';
import { useTranslate } from '../../../../hooks';
import { SHOW_OGI_NEAR_RASTER_COORDINATES, SHOW_HISTOGAM } from '../../../../environmentVariables';
import { OGITitle } from '../DataSpecific/OGINearEmissions/OGITitle';
import { useEffect, useState, SyntheticEvent } from 'react';
import { analytics, ErrorBoundary, handleErrorBoundaryError } from '../../../../utils';
import {
    METHANESAT_FAQ_URL,
    FAQ_DISPERSED_SOURCES,
    FAQ_HOW_TO_INTERPRET,
    METHANESAT_TECHNICAL_PDF_URL
} from '../../../../consts';
import { EmissionTitle } from './EmissionTitle';
import { selectMethaneProduct } from '../../../../reducers';
import StyledTabs from '../../../StyledTabs';

export const DispersedSourceAccordion = ({
    info,
    handleChange,
    expanded,
    ogiNearbyEnabled
}: DispersedSourceAccordionProps) => {
    const [ogiError, setOgiError] = useState(false);
    const [currentTab, setCurrentTab] = useState(0);

    const theme = useTheme();
    const t = useTranslate();

    const product = useSelector(selectMethaneProduct);

    const {
        id,
        title,
        emissionsHeadline,
        dataRows,
        iconColor,
        platform,
        BarGradientPropValues,
        histogramContent,
        ogiNearbyContent
    } = useGetDispersedSourceAccordionProps(info, ogiError);

    useEffect(() => {
        if (expanded) {
            analytics.expandDispersedSourceAccordion({
                emissionRate: info.object.properties.methane,
                latitude: info.coordinate?.[1],
                longitude: info.coordinate?.[0],
                platform
            });
        }
    }, [expanded, info, platform]);

    const handleTabChange = (event: SyntheticEvent, selectedTab: number) => {
        setCurrentTab(selectedTab);
    };

    const latitude = info.coordinate?.[1];
    const longitude = info.coordinate?.[0];

    const tooltip = (
        <Box sx={{ paddingBottom: 1 }}>
            <Typography variant="body2">
                {t(`emissionsMapPage.miniLegend.area-emission-raster.l4Description`)}
            </Typography>

            <Link
                href={`${METHANESAT_FAQ_URL}${FAQ_DISPERSED_SOURCES}`}
                underline="always"
                variant="body2"
                data-testid="emissions-faq-link"
                target="_blank"
                onClick={(event) => {
                    //prevent clicking the link from closing the accordion
                    event.stopPropagation();
                }}
            >
                {t(`emissionsMapPage.miniLegend.area-emission-raster.l4DescriptionLink`)}
            </Link>
        </Box>
    );

    let caveatMarkdown = t('emissionsMapPage.mapModals.dispersed.generalCaveat', {
        faq_link: `${METHANESAT_FAQ_URL}${FAQ_DISPERSED_SOURCES}`
    });
    // additional text for methaneSAT only
    if (platform === Platforms.MSAT) {
        caveatMarkdown += `\n${t('emissionsMapPage.mapModals.dispersed.msatCaveat', {
            pdf_link: METHANESAT_TECHNICAL_PDF_URL
        })}`;
    }

    const showTabs = SHOW_OGI_NEAR_RASTER_COORDINATES || SHOW_HISTOGAM;
    const getTabs = () => {
        const tabs = [t('emissionsMapPage.drawerWithCards.tabs.details')];

        if (SHOW_OGI_NEAR_RASTER_COORDINATES) tabs.push(t('emissionsMapPage.drawerWithCards.tabs.ogi'));
        if (SHOW_HISTOGAM && product === AreaEmissionsProducts.l4)
            tabs.push(t('emissionsMapPage.drawerWithCards.tabs.histogram'));

        return tabs;
    };

    const detailsContent = (
        <>
            <Box marginBottom={1}>
                <DataRowsModalBody dataRows={dataRows} />
            </Box>

            <Grid size="auto" container paddingBottom={1} marginTop={1}>
                <Grid container rowGap={1}>
                    {caveatMarkdown.split('\n').map((text, idx) => {
                        return (
                            <Grid key={idx} size={{ xs: 12 }}>
                                <Typography variant="body1" component="p">
                                    <MarkdownFormatter
                                        markdown={text}
                                        components={{
                                            a: ({ ...props }) => (
                                                <a
                                                    {...props}
                                                    style={{ color: 'inherit' }}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    data-testid={`dispersed-emissions-drawer-link-${props.key}`}
                                                />
                                            )
                                        }}
                                    />
                                </Typography>
                            </Grid>
                        );
                    })}
                </Grid>
            </Grid>
        </>
    );

    return (
        <Accordion
            key={id}
            expanded={expanded}
            onChange={() => {
                handleChange(id, info);
            }}
            data-testid={`dispersed-accordion-${id}`}
            elevation={0}
            disableGutters
            sx={theme.mixins.emissionAccordion}
            disabled={!expanded && ogiNearbyEnabled}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ padding: '0' }}>
                <EmissionTitle
                    emissionTypeLabel={title}
                    tooltip={tooltip}
                    Icon={
                        <DispersedSourceSolidIcon
                            iconColor={iconColor}
                            sx={(theme) => ({ marginRight: theme.mixins.emissionsDrawerIcon.marginRight })}
                            width={theme.mixins.emissionsDrawerIcon.width}
                            height={theme.mixins.emissionsDrawerIcon.height}
                        />
                    }
                    emissionsHeadline={emissionsHeadline}
                    data-testid={`dispersed-accordion-title-${id}`}
                />
            </AccordionSummary>

            {/* padding accounts for the icon width + marginRight */}
            <AccordionDetails
                sx={(theme) => ({
                    pl: `calc(${theme.mixins.emissionsDrawerIcon.width}px + ${theme.mixins.emissionsDrawerIcon.marginRight})`
                })}
            >
                <Box sx={{ marginBottom: 2 }}>
                    <BarGradient {...BarGradientPropValues} />
                </Box>
                {/* Show only the details if there would be a single tab */}
                {!showTabs && detailsContent}

                {/* Show the tabs if either OGI or Histogram is enabled */}
                {showTabs && (
                    <Box sx={{ width: '100%' }}>
                        <StyledTabs
                            value={currentTab}
                            onChange={handleTabChange}
                            aria-label="tabs"
                            data-testid="dispersed-tabs"
                            sx={{
                                ...(currentTab === 0 && {
                                    '& .MuiTabs-indicator': {
                                        backgroundPosition: 'bottom left'
                                    }
                                })
                            }}
                        >
                            {getTabs().map((tab, index) => {
                                return (
                                    <Tab
                                        label={tab}
                                        key={index}
                                        disabled={
                                            tab !== t('emissionsMapPage.drawerWithCards.tabs.ogi') && ogiNearbyEnabled
                                        }
                                    />
                                );
                            })}
                        </StyledTabs>
                        <TabPanel value={currentTab} index={0}>
                            {detailsContent}
                        </TabPanel>
                        {SHOW_OGI_NEAR_RASTER_COORDINATES && (
                            <ErrorBoundary
                                key={info.object.id}
                                onError={() => {
                                    setOgiError(true);
                                    handleErrorBoundaryError(`PlumeDrawerOGITitle: ${info.object.id}`);
                                }}
                                fallbackRender={({ resetErrorBoundary }) => {
                                    return (
                                        <Alert
                                            action={
                                                <Button
                                                    onClick={() => {
                                                        resetErrorBoundary();
                                                        setOgiError(false);
                                                    }}
                                                    data-testid="retry-ogc-fetch-button"
                                                    size="small"
                                                >
                                                    {t('emissionsMapPage.error.retry')}
                                                </Button>
                                            }
                                            severity="error"
                                        >
                                            {t('emissionsMapPage.error.general')}
                                        </Alert>
                                    );
                                }}
                            >
                                <TabPanel value={currentTab} index={1}>
                                    <OGITitle source="disperesed" longitude={longitude} latitude={latitude} />
                                    {product === AreaEmissionsProducts.l3 ? null : ogiNearbyContent}
                                </TabPanel>
                            </ErrorBoundary>
                        )}

                        {/* Tabs onChange is based on index, so this index must be dynamic because OGI is conditional */}
                        <TabPanel
                            value={currentTab}
                            index={getTabs().findIndex(
                                (tab) => tab === t('emissionsMapPage.drawerWithCards.tabs.histogram')
                            )}
                        >
                            {histogramContent}
                        </TabPanel>
                    </Box>
                )}
            </AccordionDetails>
        </Accordion>
    );
};
